import { WrappedObservation } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Observation';
import { WrappedHealthcareService } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/HealthcareService';
import { WrappedOrganization } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Organization';
import { WrappedServiceRequest } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ServiceRequest';
import { getIdFromReference, getReferenceId } from 'src/utils/fhir';
import { WrappedTask } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Task';
import { WrappedCondition } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Condition';
import { getConditionsByServiceRequest, getObservationsByServiceRequest } from 'src/careflow/care-coordination/inbound-referral/constants';

export class PatientGenericServiceRequestDto {
  id?: string;

  status?: string;

  referredFromId?: string;

  referredFromName?: string;

  referredToId?: string;

  referredToName?: string;

  serviceId?: string;

  serviceCode?: string;

  serviceName?: string;

  serviceOrganizationName?: string;

  // hot fix to declare prop
  created?: string;

  internalNumber?: string;

  tasks?: WrappedTask[];

  category?: any[];

  observations?: WrappedObservation[];

  conditions?: WrappedCondition[];
}

export const wrappedServiceRequestToPatientGenericServiceRequestDto = (
  serviceRequest: WrappedServiceRequest,
  organizations: WrappedOrganization[],
  healthcareServices: WrappedHealthcareService[],
  tasks: WrappedTask[],
  observations: WrappedObservation[],
  conditions: WrappedCondition[],
  configurations: any
) => {
  const dto: PatientGenericServiceRequestDto = {
    id: serviceRequest?.id,
  };

  dto.tasks = tasks.filter(
    ({ basedOn }) => !!basedOn?.some?.(({ reference }) => getReferenceId(reference) === dto.id)
  );

  if (serviceRequest?.status) {
    dto.status = serviceRequest?.status ?? '';
  }

  const perfOrgRef = serviceRequest?.performer?.filter((x) =>
    x?.reference?.startsWith('Organization')
  )?.[0];
  if (perfOrgRef && perfOrgRef?.reference) {
    const perfOrgId = getIdFromReference(perfOrgRef?.reference);
    const perfOrg = organizations.find((x) => x?.id === perfOrgId);
    if (perfOrg) {
      dto.referredToId = perfOrg?.id;
      dto.referredToName = perfOrg?.name;
    }
  }

  const reqRef = serviceRequest?.requester ?? null;
  if (reqRef?.display) {
    dto.referredFromId = reqRef?.id;
    dto.referredFromName = reqRef?.display;
  } else {
    const externalOrgTypeExt = (serviceRequest?.extension as any[])?.find(
      (e) => e.url === 'https://mycarecircle.site/fhir/extension/external-referral-org-type'
    );
    if (externalOrgTypeExt) {
      const orgTypeDisplay =
        externalOrgTypeExt.valueCodeableConcept?.text ??
        externalOrgTypeExt.valueCodeableConcept?.coding?.[0]?.display ??
        '';
      const externalOrgNameExt = (serviceRequest?.extension as any[])?.find(
        (e) => e.url === 'https://mycarecircle.site/fhir/extension/external-referral-org-name'
      );
      const orgName = externalOrgNameExt?.valueString;
      dto.referredFromName = orgName ? `${orgTypeDisplay} — ${orgName}` : orgTypeDisplay;
    }
  }

  const perfServRef = serviceRequest?.performer?.filter((x) =>
    x?.reference?.startsWith('HealthcareService')
  )?.[0];

  const performer = serviceRequest?.performer
    ?.find?.((performer) => performer?.reference?.split?.('/')?.[0] !== 'HealthcareService');

  if (perfServRef && perfServRef?.reference) {
    const perfServId = getIdFromReference(perfServRef?.reference);
    const perfServ = healthcareServices.find((x) => x?.id === perfServId); 
    if (perfServ) {
      dto.serviceId = perfServ?.id;
      dto.serviceCode = perfServ?.program?.[0]?.coding?.[0]?.code ?? '';
      dto.serviceName = perfServ?.name ?? '';
      
      const organizationId = perfServ?.providedBy?.reference?.split?.('/')?.[1];
      dto.serviceOrganizationName = organizationId
        ? organizations.find(({ id }) => id === organizationId)?.name ?? ''
        : '';
    } else {
      dto.serviceId = perfServId ?? '';
      dto.serviceCode = serviceRequest?.code?.coding?.[0]?.code ?? '';
      
      const performerDisplay = perfServRef?.display ?? '';
      
      if (performerDisplay && performerDisplay.includes('|')) {
        const parts = performerDisplay.split('|');
        dto.serviceName = parts[0]?.trim() ?? '';
        dto.serviceOrganizationName = parts[1]?.trim() ?? '';
      } else {
        dto.serviceName = performerDisplay || ((serviceRequest?.code?.coding?.[0]?.display || serviceRequest?.code?.text) ?? '');
        dto.serviceOrganizationName = '';
      }
    }
  }else if(performer) {
    dto.serviceId = serviceRequest?.code?.coding?.[0]?.code ?? '';
    dto.serviceCode = serviceRequest?.code?.coding?.[0]?.code ?? '';
    dto.serviceName = (serviceRequest?.code?.coding?.[0]?.display || serviceRequest?.code?.text) ?? '';
    dto.serviceOrganizationName = performer?.display
  }else if(!performer && serviceRequest?.code){
    dto.serviceId = serviceRequest?.code?.coding?.[0]?.code ?? '';
    dto.serviceCode = serviceRequest?.code?.coding?.[0]?.code ?? '';
    dto.serviceName = (serviceRequest?.code?.coding?.[0]?.display || serviceRequest?.code?.text) ?? '';
  }

  dto.created = serviceRequest?.authoredOn;

  dto.internalNumber =
    serviceRequest?.identifier?.find?.(
      ({ system, type }) => system === configurations?.serviceRequestInternalNumberIdenitifierSystem ||
        type?.text === configurations?.serviceRequestNATIdentifierType
    )?.value ?? '';

  const existingConditionRefs = serviceRequest?.reasonReference?.filter(
    (x) => x?.reference?.startsWith('Condition') ?? []
  );

  if (serviceRequest && 
    conditions && 
    existingConditionRefs && 
    existingConditionRefs.length > 0
  ) {
    dto.conditions = getConditionsByServiceRequest(serviceRequest, conditions);
  }

  const existingObservationRefs = serviceRequest?.reasonReference?.filter(
    (x) => x?.reference?.startsWith('Observation') ?? []
  );

  if (serviceRequest && 
    observations && 
    existingObservationRefs && 
    existingObservationRefs.length > 0
  ) {
    dto.observations = getObservationsByServiceRequest(serviceRequest, observations); 
  }

  dto.category = serviceRequest?.category ?? [];

  return dto;
};
